import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  age: number;
  life: number;
}

export function SupernovaCollapse() {
  const [phase, setPhase] = useState<number>(0); // 0=idle, 1=infall, 2=rebound, 3=explosion, 4=remnant
  const [remnantType, setRemnantType] = useState<string>('Neutron Star Remnant');

  const seqTimeRef = useRef<number>(0);
  const infallParticlesRef = useRef<Particle[]>([]);
  const explosionParticlesRef = useRef<Particle[]>([]);
  const lastPhaseRef = useRef<number>(0);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 1. SEQUENCE PHASE CONTROL TIMER
    if (phase !== 0) {
      seqTimeRef.current += deltaTime;
      const st = seqTimeRef.current;

      if (st <= 0.4) {
        if (phase !== 1) setPhase(1);
      } else if (st <= 0.6) {
        if (phase !== 2) setPhase(2);
      } else if (st <= 3.0) {
        if (phase !== 3) setPhase(3);
      } else if (st <= 5.0) {
        if (phase !== 4) setPhase(4);
      } else {
        // Automatically reset to idle after 2s remnant display
        setPhase(0);
        seqTimeRef.current = 0;
      }
    }

    const t = seqTimeRef.current;

    // Detect phase changes to trigger particle spawns
    if (phase !== lastPhaseRef.current) {
      if (phase === 1) {
        // Spawn 40 infall particles converging
        infallParticlesRef.current = [];
        for (let i = 0; i < 40; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = 180 + Math.random() * 40; // outer bounds
          const speed = dist / 0.4; // reach center in 0.4s
          infallParticlesRef.current.push({
            x: cx + Math.cos(angle) * dist,
            y: cy + Math.sin(angle) * dist,
            vx: -Math.cos(angle) * speed,
            vy: -Math.sin(angle) * speed,
            size: 3 + Math.random() * 2,
            color: '#F87171',
            age: 0,
            life: 0.4
          });
        }
      } else if (phase === 2) {
        // Reverse infall particles for outward bounce at 3x speed
        infallParticlesRef.current.forEach((p) => {
          p.vx = -p.vx * 3.0;
          p.vy = -p.vy * 3.0;
          p.life = 1.0;
          p.age = 0;
          p.color = '#FFFFFF'; // white hot
        });
      } else if (phase === 3) {
        // Spawn 150 debris particles
        explosionParticlesRef.current = [];
        const colors = ['#F97316', '#FBBF24', '#FFFFFF', '#EF4444'];
        for (let i = 0; i < 150; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 100 + Math.random() * 300; // 100 to 400 px/s
          const life = 1.0 + Math.random() * 1.4;
          explosionParticlesRef.current.push({
            x: cx,
            y: cy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            size: 3 + Math.random() * 4,
            color: colors[Math.floor(Math.random() * colors.length)],
            age: 0,
            life
          });
        }
      }
      lastPhaseRef.current = phase;
    }

    // 2. RENDER THE PHASES

    // --- PHASE 0: IDLE STATE ---
    if (phase === 0) {
      const pulse = Math.sin(totalTime * 3.0) * 1.5;
      
      // Star outer layer (orange)
      const starGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 110 + pulse);
      starGlow.addColorStop(0, 'rgba(249, 115, 22, 0.4)');
      starGlow.addColorStop(0.8, 'rgba(249, 115, 22, 0.2)');
      starGlow.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.fillStyle = starGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, 110 + pulse, 0, Math.PI * 2);
      ctx.fill();

      // Outer outline
      ctx.strokeStyle = 'rgba(249, 115, 22, 0.3)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, 110, 0, Math.PI * 2);
      ctx.stroke();

      // Iron Core (red)
      ctx.fillStyle = '#EF4444';
      ctx.beginPath();
      ctx.arc(cx, cy, 12 + pulse * 0.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Fe CORE', cx, cy + 3);

      // Label
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = '11px sans-serif';
      ctx.fillText('Massive Star in Equilibrium', cx, cy + 135);
    }

    // --- PHASE 1: INFALL ---
    if (phase === 1) {
      const p = t / 0.4; // progress 0 to 1
      const currentStarRadius = 110 - p * 90; // shrink to 20

      // Crushing star circle
      ctx.fillStyle = 'rgba(249, 115, 22, ' + (0.3 * (1 - p)) + ')';
      ctx.beginPath();
      ctx.arc(cx, cy, currentStarRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = 'rgba(249, 115, 22, ' + (0.5 * (1 - p)) + ')';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, currentStarRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Red Vignette
      const vign = ctx.createRadialGradient(cx, cy, 50, cx, cy, Math.max(cx, cy) * 1.2);
      vign.addColorStop(0, 'rgba(239, 68, 68, 0)');
      vign.addColorStop(1, 'rgba(239, 68, 68, 0.25)');
      ctx.fillStyle = vign;
      ctx.fillRect(0, 0, width, height);

      // Infall particles
      infallParticlesRef.current.forEach((p) => {
        p.x += p.vx * deltaTime;
        p.y += p.vy * deltaTime;

        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Flashing text label
      ctx.fillStyle = 'rgba(239, 68, 68, ' + (Math.sin(totalTime * 25) > 0 ? 1.0 : 0.4) + ')';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('GRAVITY WINS. COLLAPSING AT 25% SPEED OF LIGHT', cx, height - 60);
    }

    // --- PHASE 2: REBOUND (Core bounce) ---
    if (phase === 2) {
      // White shockwave burst ring
      const shockTime = t - 0.4;
      const rShock = shockTime * 500;
      const shockOpacity = Math.max(0, 1 - shockTime / 0.2);

      ctx.strokeStyle = `rgba(255, 255, 255, ${shockOpacity})`;
      ctx.lineWidth = Math.max(0.5, 6 - shockTime * 15.0);
      ctx.beginPath();
      ctx.arc(cx, cy, rShock, 0, Math.PI * 2);
      ctx.stroke();

      // Rebounding particles
      infallParticlesRef.current.forEach((p) => {
        p.x += p.vx * deltaTime;
        p.y += p.vy * deltaTime;

        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Instant white screen flash
      if (t >= 0.4 && t <= 0.44) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillRect(0, 0, width, height);
      }
    }

    // --- PHASE 3: EXPLOSION ---
    if (phase === 3) {
      const shockTime = t - 0.4;
      const rShock = shockTime * 450;
      const shockOpacity = Math.max(0, 1 - shockTime / 2.0);

      // Expanding Shockwave ring
      ctx.strokeStyle = `rgba(255, 255, 255, ${shockOpacity})`;
      ctx.lineWidth = Math.max(0.5, 6 - shockTime * 3.5);
      ctx.beginPath();
      ctx.arc(cx, cy, rShock, 0, Math.PI * 2);
      ctx.stroke();

      // Debris particles
      explosionParticlesRef.current.forEach((p) => {
        p.x += p.vx * deltaTime;
        p.y += p.vy * deltaTime;
        p.age += deltaTime;

        const ageFactor = 1 - p.age / p.life;
        if (ageFactor > 0) {
          ctx.fillStyle = p.color;
          ctx.globalAlpha = ageFactor;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * ageFactor, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      ctx.globalAlpha = 1.0; // reset

      // Large fading text label
      const textOpacity = Math.min(1.0, (t - 0.6) / 0.5);
      ctx.fillStyle = 'rgba(255, 255, 255, ' + textOpacity + ')';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('SUPERNOVA!', cx, height - 72);

      ctx.fillStyle = 'rgba(255, 255, 255, ' + textOpacity * 0.7 + ')';
      ctx.font = '11px sans-serif';
      ctx.fillText('Outshines a galaxy of 100 billion stars!', cx, height - 54);
    }

    // --- PHASE 4: REMNANT ---
    if (phase === 4) {
      if (remnantType === 'Neutron Star Remnant') {
        // Draw Neutron Star
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fill();

        // Pulsing heat glow
        const nsGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 18);
        nsGlow.addColorStop(0, 'rgba(96, 165, 250, 0.8)');
        nsGlow.addColorStop(1, 'rgba(96, 165, 250, 0)');
        ctx.fillStyle = nsGlow;
        ctx.beginPath();
        ctx.arc(cx, cy, 18, 0, Math.PI * 2);
        ctx.fill();

        // Pulsar Jets
        ctx.strokeStyle = 'rgba(147, 197, 253, 0.35)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy - 65);
        ctx.lineTo(cx, cy + 65);
        ctx.stroke();
      } else {
        // Accretion Disk (glowing dust ring)
        ctx.strokeStyle = 'rgba(249, 115, 22, 0.7)';
        ctx.lineWidth = 3.5;
        ctx.beginPath();
        ctx.ellipse(cx, cy, 20, 5, Math.PI / 12, 0, Math.PI * 2);
        ctx.stroke();

        // Black Hole core (singularity event horizon)
        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.fill();
      }

      // Remnant label
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Remnant: ${remnantType}`, cx, height - 60);
    }

    // Top-Left Info Header
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Supernova Core Collapse & Blast', 12, 24);
  }, [phase, remnantType]);

  const handleTrigger = () => {
    // Randomize remnant outcome
    setRemnantType(Math.random() > 0.5 ? 'Neutron Star Remnant' : 'Black Hole');
    setPhase(1);
    seqTimeRef.current = 0;
  };

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Button Overlay Top Right */}
      <div className="absolute top-3 right-3">
        {phase === 0 ? (
          <button
            onClick={handleTrigger}
            className="px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 transition-all duration-300"
          >
            ⚡ Trigger Collapse
          </button>
        ) : phase === 4 ? (
          <button
            onClick={handleTrigger}
            className="px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider bg-white/20 hover:bg-white/30 text-white border border-white/10 transition-all duration-300"
          >
            ↺ Again
          </button>
        ) : (
          <div className="px-4 py-2 rounded-lg font-bold text-xs uppercase bg-white/10 text-white/50 border border-white/5 cursor-not-allowed">
            Collapsing...
          </div>
        )}
      </div>
    </div>
  );
}
